// Patient sessions in priism-appt are tenant-scoped: the v2 patient JWT carries
// a `tid` (tenant) claim and can only ever read its own clinic's data — the
// backend resolves the tenant DB from the token, never from the URL. The stored
// `patientDetails.heid` records which clinic the session was minted for.
//
// If the tenant in the URL differs from the session's tenant (e.g. the patient
// signs in at /home/STHIRA, then edits the address bar to /home/MANOSHANTI), the
// stored token belongs to a different clinic. There's no cross-tenant data leak
// (the token can't read the other clinic), but leaving it in place is wrong:
//   • the UI keeps showing a "signed in" state that belongs to another clinic;
//   • any authed call silently returns the OLD clinic's data, not the new one's.
// So we drop the session on a tenant change — the patient is logged out and must
// re-authenticate for the clinic now in the URL.

// Route prefixes whose NEXT path segment is the tenant code
// (e.g. /home/<tenant>, /profile/<tenant>/<userId>, /book-an-appointment/<tenant>).
const TENANT_ROUTE_PREFIXES = new Set([
  "home",
  "profile",
  "sign-in",
  "sign-up",
  "appointment-list",
  "book-an-appointment",
  "reschedule-appointment",
]);

// The tenant code embedded in a URL path, or null if the route doesn't carry one.
export function tenantFromPath(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && TENANT_ROUTE_PREFIXES.has(parts[0])) {
    try {
      return decodeURIComponent(parts[1]);
    } catch {
      return parts[1];
    }
  }
  return null;
}

// The tenant the stored patient session belongs to (set at login from the
// reference code; see v2PatientToLegacy `heid`).
function storedSessionTenant(): string | null {
  try {
    const raw = localStorage.getItem("patientDetails");
    if (!raw || raw === "null") return null;
    const pd = JSON.parse(raw);
    return pd?.heid ?? pd?.heId ?? null;
  } catch {
    return null;
  }
}

/**
 * Clear the stored patient session if it belongs to a different tenant than the
 * one in the current URL. Returns true if a session was cleared. Safe to call on
 * every load / navigation — it no-ops when there's no session, the route carries
 * no tenant, or the tenants match (case-insensitive).
 */
export function enforcePatientTenant(pathname: string = window.location.pathname): boolean {
  const urlTenant = tenantFromPath(pathname);
  if (!urlTenant) return false;
  const sessionTenant = storedSessionTenant();
  if (!sessionTenant) return false;
  if (sessionTenant.toLowerCase() === urlTenant.toLowerCase()) return false;

  // A brand group code and its member branches are ONE clinic family: the linking
  // site may mint the session under the brand code (e.g. "MANOSHANTI") and later
  // route to a branch code ("manoshanti_bengaluru"), or vice-versa. That is NOT a
  // cross-tenant switch — the patient JWT identifies the patient globally (it is
  // not tenant-bound) and the data calls scope by (brand, mobile) server-side. So
  // only a genuine BRAND change (manoshanti ↔ sthira) drops the session; compare
  // the brand root (the code segment before the first underscore).
  const brandRoot = (c: string) => c.toLowerCase().split("_")[0];
  if (brandRoot(sessionTenant) && brandRoot(sessionTenant) === brandRoot(urlTenant)) return false;

  // Cross-tenant session — drop it (logout) and point the reference code at the
  // clinic now in the URL so the next auth happens against the right tenant.
  localStorage.removeItem("authToken");
  localStorage.removeItem("patientDetails");
  localStorage.removeItem("isAuthenticated");
  localStorage.setItem("referenceCode", urlTenant);
  return true;
}
